// Settings ▸ Creative catalog: the values the game's specs may use (characters, weapons, enemies, bosses, hooks,
// CTAs…) and the reference of the spec (actions, variation dimensions, safe areas).

import type { CreativeCatalog } from '@growth/client';
import { useState } from 'react';
import { Card, DataTable, ErrorBox, Icon } from '../components/ui.tsx';
import { invalidateCache, useAction, useSession } from '../session.tsx';
import { useCatalog } from './catalog.ts';

const ORDER = ['directors', 'scenes', 'scenarios', 'maps', 'characters', 'skins', 'weapons', 'enemy_types', 'bosses', 'camera_presets', 'spawn_patterns', 'actions', 'danger_actions', 'hooks', 'ctas', 'music'];

/** The catalog in a readable order (the database returns the keys in its own order). */
function ordered(c: CreativeCatalog): CreativeCatalog {
  const keys = Object.keys(c).sort((a, b) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99));
  return Object.fromEntries(keys.map((k) => [k, (c as Record<string, unknown>)[k]])) as CreativeCatalog;
}

const ACTIONS_ES: Record<string, string> = {
  near_death: 'el jugador está a punto de morir (la vida baja casi a cero) y sobrevive',
  power_up: 'una mejora visible (objeto, carta de mejora, evolución)',
  level_up: 'subida de nivel',
  evolve: 'evolución del arma o del personaje',
  boss_kill: 'cae el jefe',
  win: 'la partida termina en victoria',
  fail: 'la partida termina en derrota',
};

const EXAMPLE: CreativeCatalog = {
  directors: ['antpocalypse'],
  characters: ['rita', 'carmen'],
  weapons: ['insecticida', 'chancla'],
  enemy_types: ['obrera', 'soldado'],
  bosses: ['reina'],
  maps: ['sabana'],
  spawn_patterns: ['surround', 'front'],
  hooks: [{ id: 'survive-60', type: 'question', text: { es: '¿Sobrevivirías 60 segundos?', en: 'Could you survive 60 seconds?' } }],
  ctas: [{ id: 'play-free', text: { es: '¡JUEGA GRATIS!', en: 'PLAY FREE!' } }],
};

function CatalogEditor({ gameId, catalog }: { gameId: string; catalog: CreativeCatalog }) {
  const { api, isAdmin } = useSession();
  const action = useAction();
  const empty = Object.keys(catalog).length === 0;
  const [text, setText] = useState(JSON.stringify(empty ? EXAMPLE : ordered(catalog), null, 2));
  const [saved, setSaved] = useState(false);
  const save = async () => {
    setSaved(false);
    let parsed: CreativeCatalog;
    try {
      parsed = JSON.parse(text) as CreativeCatalog;
    } catch (e) {
      action.setError(`JSON no válido: ${(e as Error).message}`);
      return;
    }
    if (await action.run(() => api.setCreativeCatalog(gameId, parsed))) {
      invalidateCache(`catalog:${gameId}`);
      setSaved(true);
    }
  };
  return (
    <Card
      title="Catálogo creativo"
      subtitle={
        empty
          ? 'Vacío: los specs aceptan cualquier valor. Abajo, un ejemplo para empezar (no se guarda hasta que pulses Guardar).'
          : 'Los specs solo pueden usar estos valores (una lista vacía no se comprueba). Los ganchos y CTAs alimentan las variaciones.'
      }
    >
      <div className="stack">
        <textarea className="input code" rows={20} spellCheck={false} value={text} onChange={(e) => setText(e.target.value)} readOnly={!isAdmin} aria-label="Catálogo (JSON)" />
        {action.error && <ErrorBox error={action.error} />}
        {saved && (
          <span className="badge">
            <Icon name="good" />
            Guardado
          </span>
        )}
        {isAdmin && (
          <div className="row">
            <button type="button" className="btn primary" onClick={save} disabled={action.busy}>
              Guardar
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

export function CreativeCatalogTab({ gameId }: { gameId: string }) {
  const c = useCatalog(gameId);
  if (c.error) return <ErrorBox error={c.error} onRetry={c.reload} />;
  if (!c.data) return null;
  const ref = c.data.reference;
  return (
    <div className="stack">
      <CatalogEditor key={JSON.stringify(c.data.catalog)} gameId={gameId} catalog={c.data.catalog} />
      <div className="grid cols-2">
        <Card title="Acciones estándar de los beats" subtitle="El director del juego implementa las que tiene; el juego puede añadir las suyas (lista actions).">
          <DataTable compact data={{ columns: [{ label: 'Acción' }, { label: 'Qué hace' }], rows: Object.entries(ref.standard_actions).map(([k, v]) => [<code>{k}</code>, ACTIONS_ES[k] ?? v]) }} />
        </Card>
        <Card title="Dimensiones de las variaciones" subtitle="Cada variación cambia una sola, por turnos y con el mismo seed.">
          <DataTable compact data={{ columns: [{ label: 'Id' }, { label: 'Dimensión' }], rows: ref.dimensions.map((d) => [<code>{d.id}</code>, d.label]) }} />
        </Card>
      </div>
      <Card title="Zonas seguras" subtitle="Plantillas conservadoras (no hay una cifra oficial única): los textos se recolocan dentro. Revisa siempre la vista previa de cada red.">
        <DataTable
          compact
          data={{
            columns: [{ label: 'Plantilla' }, { label: 'Formato' }, { label: 'Arriba', numeric: true }, { label: 'Abajo', numeric: true }, { label: 'Izquierda', numeric: true }, { label: 'Derecha', numeric: true }, { label: 'Nota' }],
            rows: ref.safe_areas.map((a) => [a.label, a.format, `${Math.round(a.top * 100)} %`, `${Math.round(a.bottom * 100)} %`, `${Math.round(a.left * 100)} %`, `${Math.round(a.right * 100)} %`, a.note]),
          }}
        />
      </Card>
    </div>
  );
}
